import { useNavigate, useParams } from 'react-router-dom';
import styled, { useTheme } from 'styled-components/macro';
import { WxButton, WxLabel, WxText } from '../components';
import { OrderItemCard } from '../components/pages/order/OrderItemCard';
import { AddressDisplay } from '../components/ui/address/AddressDisplay';
import { CustomNav } from '../components/ui/customNav/CustomNav';
import { AddressType } from '../hooks/api/queries/useAccountAddress';
import useOrder, {
  ProviderOrderStatuses,
  providerOrderStatusesLabels,
} from '../hooks/api/queries/useOrder';
import useJwt from '../hooks/utils/useJwt';
import { __ } from '../i18n/l10n';
import { breakpoint } from '../styles/constants';
import { SLUG_ORDERS } from '../styles/slugs';
import { appendCommitHash } from '../utils/appendHash';
import { getDateFormatted } from '../utils/getDateFormatted';
import { splitNumber } from '../utils/splitNumber';
import { PageWrapper } from './Category';

export const Order = () => {
  const { orderId } = useParams();
  const { data: order } = useOrder(parseInt(orderId ?? ''));
  const navigate = useNavigate();
  const jwt = useJwt();
  const theme = useTheme();

  if (!jwt) navigate(appendCommitHash('/'));

  if (!order || order.providerOrders.length === 0) {
    navigate(`/${SLUG_ORDERS}`);
    return null;
  }

  const address: AddressType = {
    firstname: order.firstname,
    lastname: order.lastname,
    address1: order.address1,
    zipcode: order.zipcode,
    city: order.city,
    mobilePhone: order.mobilePhone,
  };

  return (
    <PageWrapper backgroundColor={theme.colors.black}>
      <CustomNav
        label={__('Order')}
        navStyle={'chevron'}
        forceDesktopLabelCenter
      />
      <RedText>
        {order.providerOrders.length > 1 && (
          <WxText
            text={__('Your order will be shipped in multiple shipments')}
            align={'center'}
            bold
          />
        )}
      </RedText>
      {order.providerOrders
        ?.filter(
          (providerOrder) =>
            providerOrder.status !== ProviderOrderStatuses['280-RETRIED'],
        )
        ?.map((providerOrder, index) => {
          return (
            <DivContent key={providerOrder.id}>
              <div>
                <DivBlock>
                  <WxText text={__('Order status')} bold darkMode />
                  <WxLabel
                    label={providerOrderStatusesLabels[providerOrder.status]}
                  />
                  {index === 0 && (
                    <>
                      <br />
                      <WxText
                        darkMode
                        text={`${__(
                          'Order number',
                        )} : \n${order.id.toString()}`}
                      />
                      <WxText
                        darkMode
                        text={getDateFormatted(order.createdAt)}
                      />
                    </>
                  )}
                </DivBlock>

                {index === 0 && (
                  <DivBlock>
                    <>
                      <WxText text={__('Address')} bold darkMode />
                      <AddressDisplay address={address} />
                    </>
                  </DivBlock>
                )}
              </div>
              <DivArticles>
                <div>
                  <WxText text={__('Items')} bold darkMode />
                  <WxText
                    text={`${providerOrder.orderItems?.reduce(
                      (acc, current) => acc + current.quantity,
                      0,
                    )}`}
                    bold
                    darkMode
                  />
                </div>
                <ul>
                  {providerOrder?.orderItems?.map((item, i) => (
                    <li key={`order-item-${i}-${item.id}`}>
                      <Separator />
                      <OrderItemCard item={item} />
                    </li>
                  ))}
                </ul>
              </DivArticles>
            </DivContent>
          );
        })}

      <DivTotal>
        <div>
          <WxLabel label="Total" />
          <WxLabel
            label={`${splitNumber(order.totalMilesAmount)} ${__('MILES')}`}
          />
        </div>
        <div>
          <WxText text={__('Delivery')} darkMode />
          <WxText text={__('Included')} darkMode />
        </div>
        <WxButton
          label={__('Contact support')}
          onClick={() => {
            const email =
              window.config?.LICENSE === 'DE'
                ? 'support@winamax.de '
                : window.config?.LICENSE === 'FR'
                ? 'soporte@winamax.es'
                : 'support@winamax.fr';
            const link = 'mailto:' + email;
            window.location.href = link;
          }}
          type={'neutral'}
          noArrow
          expand
        />
      </DivTotal>
    </PageWrapper>
  );
};

const Separator = styled.div`
  width: 100%;
  border-top: 1px solid ${({ theme }) => theme.colors.darkgray};
  margin-top: 1.25rem;
  margin-bottom: 1.25rem;
`;

const DivContent = styled.div`
  padding-top: 1.25rem;
  > div:first-child {
    display: flex;
    flex-direction: column;
    gap: 2.5rem;
    margin-bottom: 2.5rem;

    @media (min-width: ${breakpoint}) {
      gap: 3.75rem;
    }
  }
  padding-bottom: 1.25rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.white};

  @media (min-width: ${breakpoint}) {
    display: flex;
    flex-direction: row;
    padding-top: 3.75rem;

    > div {
      width: 50%;
    }
  }
`;

const DivBlock = styled.div`
  > :first-child {
    margin-bottom: 0.625rem;
  }
`;

const DivArticles = styled.div`
  > :first-child {
    display: flex;
    justify-content: space-between;
    margin-bottom: 0.625rem;
  }
`;

const DivTotal = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  padding-top: 0.625rem;

  > :first-child {
    > :last-child {
      color: ${({ theme }) => theme.colors.red};
    }
  }

  > div {
    display: flex;
    justify-content: space-between;
  }

  @media (min-width: ${breakpoint}) {
    min-width: 450px;
    margin-left: auto;
    margin-bottom: 3.75rem;
    padding-top: 1.5625rem;
  }
`;

export const RedText = styled.div`
  width: 100%;
  > div {
    margin-inline: auto;
  }
  p {
    color: ${({ theme }) => theme.colors.red};
  }
`;
